import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * Format date/time for audit display
 */
const formatAuditDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Get badge color for action type
 */
const getActionBadge = (actionType) => {
  switch (actionType) {
    case 'CREATE':
      return { bg: '#d4edda', text: '#155724', label: 'Created' };
    case 'UPDATE':
      return { bg: '#cce5ff', text: '#004085', label: 'Updated' };
    case 'DELETE':
      return { bg: '#f8d7da', text: '#721c24', label: 'Deleted' };
    case 'BULK_DELETE':
      return { bg: '#f8d7da', text: '#721c24', label: 'Bulk Delete' };
    case 'IMPORT':
      return { bg: '#fff3cd', text: '#856404', label: 'Imported' };
    default:
      return { bg: '#e2e3e5', text: '#383d41', label: actionType };
  }
};

/**
 * Get icon for entity type
 */
const getEntityIcon = (entityType) => {
  switch (entityType) {
    case 'contractor': return '🏢';
    case 'contact': return '👤';
    case 'evaluation': return '⭐';
    case 'comment': return '💬';
    case 'win_loss': return '🏆';
    case 'attachment': return '📎';
    case 'insurance': return '🛡️';
    case 'province': return '📍';
    case 'weights': return '⚖️';
    default: return '📋';
  }
};

/**
 * Format entity type for display
 */
const formatEntityType = (entityType) => {
  switch (entityType) {
    case 'contractor': return 'Contractor';
    case 'contact': return 'Contact';
    case 'evaluation': return 'Evaluation';
    case 'comment': return 'Comment';
    case 'win_loss': return 'Win/Loss';
    case 'attachment': return 'Attachment';
    case 'insurance': return 'Insurance';
    case 'province': return 'Province';
    case 'weights': return 'Weights';
    default: return entityType;
  }
};

/**
 * Render JSON changes in a readable format
 */
const ChangesDisplay = ({ changes, isExpanded }) => {
  if (!changes || Object.keys(changes).length === 0) {
    return <span style={{ color: '#999', fontStyle: 'italic' }}>No details</span>;
  }

  // For collapsed view, show summary
  if (!isExpanded) {
    const keys = Object.keys(changes);
    if (changes.before && changes.after) {
      const changedFields = Object.keys(changes.before);
      return (
        <span style={{ color: '#666', fontSize: '12px' }}>
          Changed: {changedFields.join(', ')}
        </span>
      );
    }
    if (changes.created) {
      return <span style={{ color: '#666', fontSize: '12px' }}>New entry created</span>;
    }
    if (changes.deleted) {
      return <span style={{ color: '#666', fontSize: '12px' }}>Entry deleted</span>;
    }
    if (changes.summary) {
      return (
        <span style={{ color: '#666', fontSize: '12px' }}>
          Import: {changes.summary.imported} added, {changes.summary.updated} updated, {changes.summary.deleted} deleted
        </span>
      );
    }
    if (changes.deleted_count) {
      return (
        <span style={{ color: '#666', fontSize: '12px' }}>
          Bulk deleted {changes.deleted_count} contractor(s)
        </span>
      );
    }
    return <span style={{ color: '#666', fontSize: '12px' }}>{keys.length} field(s) changed</span>;
  }

  // For expanded view, show full details
  return (
    <div style={{ 
      backgroundColor: '#f8f9fa', 
      padding: '8px 12px', 
      borderRadius: '4px',
      fontSize: '12px',
      fontFamily: 'monospace',
      maxHeight: '200px',
      overflow: 'auto'
    }}>
      {changes.before && changes.after && (
        <>
          <div style={{ marginBottom: '8px' }}>
            <strong style={{ color: '#dc3545' }}>Before:</strong>
            <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(changes.before, null, 2)}
            </pre>
          </div>
          <div>
            <strong style={{ color: '#28a745' }}>After:</strong>
            <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(changes.after, null, 2)}
            </pre>
          </div>
        </>
      )}
      {changes.created && (
        <div>
          <strong style={{ color: '#28a745' }}>Created:</strong>
          <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {JSON.stringify(changes.created, null, 2)}
          </pre>
        </div>
      )}
      {changes.deleted && (
        <div>
          <strong style={{ color: '#dc3545' }}>Deleted:</strong>
          <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {JSON.stringify(changes.deleted, null, 2)}
          </pre>
        </div>
      )}
      {changes.summary && (
        <div>
          <strong>Import Summary:</strong>
          <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(changes.summary, null, 2)}
          </pre>
        </div>
      )}
      {changes.deleted_contractors && (
        <div>
          <strong style={{ color: '#dc3545' }}>Deleted Contractors:</strong>
          <pre style={{ margin: '4px 0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {JSON.stringify(changes.deleted_contractors, null, 2)}
          </pre>
        </div>
      )}
      {/* Handle other change types */}
      {!changes.before && !changes.after && !changes.created && !changes.deleted && 
       !changes.summary && !changes.deleted_contractors && (
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {JSON.stringify(changes, null, 2)}
        </pre>
      )}
    </div>
  );
};

/**
 * Extract contractor name from changes when contractor_name is null
 * This handles cases like DELETE, BULK_DELETE where the contractor is gone
 */
const getContractorDisplayName = (entry) => {
  // If we have a direct contractor_name, use it
  if (entry.contractor_name) {
    return { name: entry.contractor_name, fromChanges: false };
  }
  
  // Try to extract from changes
  const changes = entry.changes;
  if (!changes) return { name: null, fromChanges: false };
  
  // For single DELETE - check deleted.contractor
  if (changes.deleted?.contractor) {
    return { name: changes.deleted.contractor, fromChanges: true };
  }
  
  // For BULK_DELETE - show count or first few names
  if (changes.deleted_contractors && Array.isArray(changes.deleted_contractors)) {
    const contractors = changes.deleted_contractors;
    if (contractors.length === 1) {
      return { name: contractors[0].contractor, fromChanges: true };
    } else if (contractors.length > 1) {
      const names = contractors.slice(0, 2).map(c => c.contractor).join(', ');
      const suffix = contractors.length > 2 ? ` +${contractors.length - 2} more` : '';
      return { name: names + suffix, fromChanges: true };
    }
  }
  
  // For CREATE - check created.contractor
  if (changes.created?.contractor) {
    return { name: changes.created.contractor, fromChanges: true };
  }
  
  return { name: null, fromChanges: false };
};

/**
 * Audit Row Component
 */
const AuditRow = ({ entry, isExpanded, onToggle }) => {
  const actionBadge = getActionBadge(entry.action_type);
  const entityIcon = getEntityIcon(entry.entity_type);
  const { name: contractorDisplayName, fromChanges } = getContractorDisplayName(entry);
  
  return (
    <div style={{
      padding: '12px 16px',
      borderBottom: '1px solid #e9ecef',
      backgroundColor: isExpanded ? '#f8f9fa' : '#fff',
      cursor: 'pointer'
    }} onClick={onToggle}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Expand/Collapse indicator */}
        <span style={{ 
          color: '#666', 
          fontSize: '12px',
          width: '16px',
          flexShrink: 0
        }}>
          {isExpanded ? '▼' : '▶'}
        </span>
        
        {/* Date/Time */}
        <div style={{ width: '140px', flexShrink: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: '500' }}>
            {formatAuditDate(entry.created_at)}
          </div>
        </div>
        
        {/* Action Badge */}
        <div style={{ width: '90px', flexShrink: 0 }}>
          <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: actionBadge.bg,
            color: actionBadge.text
          }}>
            {actionBadge.label}
          </span>
        </div>
        
        {/* Entity Type */}
        <div style={{ width: '100px', flexShrink: 0 }}>
          <span style={{ fontSize: '13px' }}>
            {entityIcon} {formatEntityType(entry.entity_type)}
          </span>
        </div>
        
        {/* Contractor Name */}
        <div style={{ width: '150px', flexShrink: 0 }}>
          <span style={{ 
            fontSize: '13px',
            color: contractorDisplayName ? '#333' : '#999',
            fontStyle: contractorDisplayName ? (fromChanges ? 'italic' : 'normal') : 'italic'
          }}>
            {contractorDisplayName || 'N/A'}
          </span>
        </div>
        
        {/* User */}
        <div style={{ width: '150px', flexShrink: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: '500' }}>
            {entry.user_name || 'Unknown'}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {entry.user_email}
          </div>
        </div>
        
        {/* Changes Summary */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ChangesDisplay changes={entry.changes} isExpanded={isExpanded} />
        </div>
      </div>
    </div>
  );
};

/**
 * ContractorAuditModal - Modal to display audit log entries
 */
const ContractorAuditModal = ({ isOpen, onClose, instance, accounts }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Filters
  const [filters, setFilters] = useState({
    entity_type: '',
    action_type: '',
    start_date: '',
    end_date: '',
    user_email: ''
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  });
  
  const modalRef = useRef(null);

  // Fetch audit entries
  const fetchAuditEntries = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', pagination.limit);
      
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_email) params.append('user_email', filters.user_email);
      
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/contractors/audit?${params.toString()}`);
      
      if (response.data.success) {
        setEntries(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          page: response.data.pagination?.page || 1,
          total: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.totalPages || 0
        }));
      } else {
        setError(response.data.message || 'Failed to fetch audit log');
      }
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setError(err.message || 'Failed to fetch audit log');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit, instance, accounts]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchAuditEntries(1);
    }
  }, [isOpen, fetchAuditEntries]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    setExpandedRows(new Set());
    fetchAuditEntries(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      entity_type: '',
      action_type: '',
      start_date: '',
      end_date: '',
      user_email: ''
    });
    setExpandedRows(new Set());
    // Fetch will happen via useEffect when filters change
  };

  // Toggle row expansion
  const toggleRowExpansion = (entryId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (entries.length === 0) return;
    
    const headers = ['Date', 'Action', 'Entity Type', 'Contractor', 'User Name', 'User Email', 'Changes'];
    const rows = entries.map(entry => [
      formatAuditDate(entry.created_at),
      entry.action_type,
      formatEntityType(entry.entity_type),
      entry.contractor_name || 'N/A',
      entry.user_name || 'Unknown',
      entry.user_email,
      JSON.stringify(entry.changes || {})
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `contractor_audit_log_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div 
        ref={modalRef}
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              📋 Contractor Audit Log
            </h2>
            <p style={{ margin: '4px 0 0', color: '#666', fontSize: '13px' }}>
              Complete history of all contractor-related changes
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
          >
            ×
          </button>
        </div>

        {/* Filters */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'flex-end',
          backgroundColor: '#fafafa'
        }}>
          {/* Entity Type Filter */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>
              Entity Type
            </label>
            <select
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '13px',
                minWidth: '120px'
              }}
            >
              <option value="">All Types</option>
              <option value="contractor">Contractor</option>
              <option value="contact">Contact</option>
              <option value="evaluation">Evaluation</option>
              <option value="comment">Comment</option>
              <option value="win_loss">Win/Loss</option>
              <option value="attachment">Attachment</option>
              <option value="insurance">Insurance</option>
              <option value="province">Province</option>
              <option value="weights">Weights</option>
            </select>
          </div>

          {/* Action Type Filter */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>
              Action
            </label>
            <select
              value={filters.action_type}
              onChange={(e) => handleFilterChange('action_type', e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '13px',
                minWidth: '120px'
              }}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Created</option>
              <option value="UPDATE">Updated</option>
              <option value="DELETE">Deleted</option>
              <option value="BULK_DELETE">Bulk Delete</option>
              <option value="IMPORT">Import</option>
            </select>
          </div>

          {/* Start Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>
              From Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '13px'
              }}
            />
          </div>

          {/* End Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>
              To Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '13px'
              }}
            />
          </div>

          {/* User Email */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase' }}>
              User Email
            </label>
            <input
              type="text"
              value={filters.user_email}
              onChange={(e) => handleFilterChange('user_email', e.target.value)}
              placeholder="Search by email..."
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                fontSize: '13px',
                width: '180px'
              }}
            />
          </div>

          {/* Filter Buttons */}
          <button
            onClick={applyFilters}
            style={{
              padding: '6px 16px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#007bff',
              color: '#fff',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            style={{
              padding: '6px 16px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              backgroundColor: '#fff',
              color: '#666',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Clear
          </button>

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Export Button */}
          <button
            onClick={exportToCSV}
            disabled={entries.length === 0}
            style={{
              padding: '6px 16px',
              borderRadius: '4px',
              border: '1px solid #28a745',
              backgroundColor: '#28a745',
              color: '#fff',
              fontSize: '13px',
              fontWeight: '500',
              cursor: entries.length === 0 ? 'not-allowed' : 'pointer',
              opacity: entries.length === 0 ? 0.6 : 1
            }}
          >
            📥 Export CSV
          </button>
        </div>

        {/* Results count */}
        <div style={{
          padding: '8px 20px',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: '#fff',
          fontSize: '13px',
          color: '#666'
        }}>
          {loading ? 'Loading...' : `${pagination.total} audit entries found`}
        </div>

        {/* Table Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '10px 16px',
          borderBottom: '2px solid #dee2e6',
          backgroundColor: '#f8f9fa',
          fontSize: '12px',
          fontWeight: '600',
          color: '#495057',
          textTransform: 'uppercase'
        }}>
          <span style={{ width: '16px' }}></span>
          <span style={{ width: '140px' }}>Date/Time</span>
          <span style={{ width: '90px' }}>Action</span>
          <span style={{ width: '100px' }}>Entity</span>
          <span style={{ width: '150px' }}>Contractor</span>
          <span style={{ width: '150px' }}>User</span>
          <span style={{ flex: 1 }}>Changes</span>
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: '#666' 
            }}>
              Loading audit log...
            </div>
          )}
          
          {error && (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: '#dc3545' 
            }}>
              Error: {error}
            </div>
          )}
          
          {!loading && !error && entries.length === 0 && (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: '#666' 
            }}>
              No audit entries found
            </div>
          )}
          
          {!loading && !error && entries.length > 0 && (
            entries.map(entry => (
              <AuditRow
                key={entry.id}
                entry={entry}
                isExpanded={expandedRows.has(entry.id)}
                onToggle={() => toggleRowExpansion(entry.id)}
              />
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#f8f9fa'
          }}>
            <button
              onClick={() => fetchAuditEntries(1)}
              disabled={pagination.page === 1}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: '#fff',
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                opacity: pagination.page === 1 ? 0.5 : 1
              }}
            >
              First
            </button>
            <button
              onClick={() => fetchAuditEntries(pagination.page - 1)}
              disabled={pagination.page === 1}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: '#fff',
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                opacity: pagination.page === 1 ? 0.5 : 1
              }}
            >
              ← Prev
            </button>
            <span style={{ padding: '0 12px', fontSize: '13px', color: '#666' }}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => fetchAuditEntries(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: '#fff',
                cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                opacity: pagination.page === pagination.totalPages ? 0.5 : 1
              }}
            >
              Next →
            </button>
            <button
              onClick={() => fetchAuditEntries(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                backgroundColor: '#fff',
                cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                opacity: pagination.page === pagination.totalPages ? 0.5 : 1
              }}
            >
              Last
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractorAuditModal;
